<script setup>
import PageGuidance from '@/components/guidance/PageGuidance.vue'
import PageHeader from '@/components/shared/PageHeader.vue'
import StatusChip from '@/components/shared/StatusChip.vue'
import { formatCurrency, formatDate } from '@core/utils/formatters'
import { relationshipLifecycleChipLabel } from '@/stores/clients'
import { useDashboardStore } from '@/stores/dashboard'

definePage({
  meta: {
    navActiveLink: 'dashboard',
  },
})

const store = useDashboardStore()
const router = useRouter()
const { t } = useI18n()

const refresh = () => store.fetchOverview().catch(() => null)

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible' && store.loaded)
    refresh()
}

onMounted(() => {
  refresh()
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onActivated(() => {
  if (store.loaded)
    refresh()
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
})

const openRoster = (state = '') => {
  const query = state ? { relationship_lifecycle_state: state } : {}

  router.push({ name: 'clients', query })
}

const openAppointments = () => {
  router.push({ name: 'appointments' })
}

const formatTimeRange = item => {
  const opts = { hour: '2-digit', minute: '2-digit' }
  const start = formatDate(item.starts_at, opts, 'nl')
  const end = formatDate(item.ends_at, opts, 'nl')

  return `${start} – ${end}`
}

const sectionMessage = err => {
  if (!err)
    return ''
  if (err === 'unavailable')
    return t('dashboard.sectionUnavailable')

  return err
}

const rosterRows = computed(() => [
  {
    state: 'prospect',
    count: store.prospectCount,
  },
  {
    state: 'active',
    count: store.activeCount,
  },
  {
    state: 'paused',
    count: store.pausedCount,
  },
])

const statsRows = computed(() => [
  {
    key: 'active',
    label: t('dashboard.stats.activeClients'),
    count: store.stats.active_clients,
    aria: t('dashboard.stats.activeClientsAria', { count: store.stats.active_clients }),
    to: { name: 'clients', query: { relationship_lifecycle_state: 'active' } },
  },
  {
    key: 'sessions',
    label: t('dashboard.stats.sessionsThisWeek'),
    count: store.stats.sessions_this_week,
    aria: t('dashboard.stats.sessionsThisWeekAria', { count: store.stats.sessions_this_week }),
    to: { name: 'appointments' },
  },
  {
    key: 'outstanding',
    label: t('dashboard.stats.outstanding'),
    count: store.stats.outstanding_count,
    aria: t('dashboard.stats.outstandingAria', { count: store.stats.outstanding_count }),
    to: { name: 'clients' },
  },
])

const moreOverdueCount = computed(() =>
  Math.max(0, (store.needsAttention.overdue_invoices_total || 0)
    - (store.needsAttention.overdue_invoices?.length || 0)))

const morePastDueCount = computed(() =>
  Math.max(0, (store.needsAttention.past_due_subscriptions_total || 0)
    - (store.needsAttention.past_due_subscriptions?.length || 0)))

const moreRiskCount = computed(() =>
  Math.max(0, (store.needsAttention.elevated_no_show_risks_total || 0)
    - (store.needsAttention.elevated_no_show_risks?.length || 0)))

const moreInsightsCount = computed(() =>
  Math.max(0, (store.needsAttention.coaching_insights_total || 0)
    - (store.needsAttention.coaching_insights?.length || 0)))

const moreCoachingAttentionCount = computed(() =>
  Math.max(0, (store.coachingAttention.total || 0)
    - (store.coachingAttention.items?.length || 0)))

const showInitialLoading = computed(() => store.loading && !store.loaded)

const openInsights = () => {
  router.push({ name: 'insights' })
}

const gapSummary = gap => {
  if (!gap)
    return ''

  if (gap.subtype === 'habit') {
    return t('dashboard.coachingAttention.gap.habit', {
      done: gap.days_with_any_done ?? 0,
      expected: gap.period_days ?? store.coachingAttention.period?.days ?? 7,
    })
  }

  const key = gap.subtype === 'nutrition'
    ? 'dashboard.coachingAttention.gap.nutrition'
    : 'dashboard.coachingAttention.gap.workout'

  return t(key, {
    done: gap.done_count ?? 0,
    expected: gap.expected_count ?? 0,
  })
}

const gapsLabel = item => {
  const gaps = Array.isArray(item?.gaps) ? item.gaps : []

  return gaps.map(gapSummary).filter(Boolean).join(' · ')
}

const worstSeverity = item => {
  const gaps = Array.isArray(item?.gaps) ? item.gaps : []
  if (gaps.some(g => g.severity === 'very_behind'))
    return 'very_behind'

  return 'behind'
}

const severityLabel = severity =>
  t(`dashboard.coachingAttention.severity.${severity === 'very_behind' ? 'very_behind' : 'behind'}`)
</script>

<template>
  <div>
    <PageHeader
      :title="$t('dashboard.title')"
      :subtitle="$t('dashboard.subtitle')"
    >
      <template #actions>
        <PageGuidance />
      </template>
    </PageHeader>

    <div
      v-if="showInitialLoading"
      class="text-medium-emphasis mb-6"
      role="status"
    >
      {{ $t('dashboard.loading') }}
    </div>

    <VRow>
      <!-- Stats -->
      <VCol
        cols="12"
        md="4"
      >
        <VCard
          flat
          border
          class="h-100"
          data-tour="dashboard-stats"
        >
          <VCardItem>
            <VCardTitle>{{ $t('dashboard.stats.title') }}</VCardTitle>
            <VCardSubtitle>{{ $t('dashboard.stats.subtitle') }}</VCardSubtitle>
          </VCardItem>
          <VCardText>
            <VAlert
              v-if="store.statsError"
              type="error"
              variant="tonal"
              role="alert"
            >
              {{ sectionMessage(store.statsError) }}
              <template #append>
                <VBtn
                  variant="text"
                  size="small"
                  :aria-label="$t('dashboard.retry')"
                  @click="refresh"
                >
                  {{ $t('dashboard.retry') }}
                </VBtn>
              </template>
            </VAlert>
            <div
              v-else
              class="d-flex flex-column gap-4"
              aria-live="polite"
            >
              <RouterLink
                v-for="row in statsRows"
                :key="row.key"
                :to="row.to"
                class="d-flex align-center justify-space-between text-decoration-none text-high-emphasis"
                :aria-label="row.aria"
              >
                <span>{{ row.label }}</span>
                <span class="text-h5 font-weight-medium">{{ row.count }}</span>
              </RouterLink>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Today's schedule -->
      <VCol
        cols="12"
        md="8"
      >
        <VCard
          flat
          border
          class="h-100"
          data-tour="dashboard-schedule"
        >
          <VCardItem>
            <VCardTitle>{{ $t('dashboard.schedule.title') }}</VCardTitle>
            <VCardSubtitle>{{ $t('dashboard.schedule.subtitle') }}</VCardSubtitle>
          </VCardItem>
          <VCardText>
            <div
              v-if="showInitialLoading"
              class="text-medium-emphasis"
              role="status"
            >
              {{ $t('dashboard.schedule.loading') }}
            </div>

            <VAlert
              v-else-if="store.scheduleError"
              type="error"
              variant="tonal"
              role="alert"
            >
              {{ sectionMessage(store.scheduleError) }}
              <template #append>
                <VBtn
                  variant="text"
                  size="small"
                  :aria-label="$t('dashboard.retry')"
                  @click="refresh"
                >
                  {{ $t('dashboard.retry') }}
                </VBtn>
              </template>
            </VAlert>

            <div
              v-else-if="store.isScheduleEmpty"
              role="status"
            >
              <VAlert
                type="info"
                variant="tonal"
                class="mb-4"
              >
                {{ $t('dashboard.schedule.empty') }}
              </VAlert>
              <VBtn
                color="primary"
                @click="openAppointments"
              >
                {{ $t('dashboard.schedule.emptyCta') }}
              </VBtn>
            </div>

            <div
              v-else
              aria-live="polite"
            >
              <VList
                class="pa-0"
                lines="two"
              >
                <VListItem
                  v-for="appointment in store.todayAppointments"
                  :key="appointment.id"
                  :to="{ name: 'appointments' }"
                  :aria-label="$t('dashboard.schedule.rowAria', {
                    client: appointment.client_name || $t('appointments.unknownClient'),
                    time: formatTimeRange(appointment),
                    status: appointment.status,
                  })"
                >
                  <VListItemTitle>
                    {{ appointment.client_name || $t('appointments.unknownClient') }}
                  </VListItemTitle>
                  <VListItemSubtitle>
                    {{ formatTimeRange(appointment) }}
                  </VListItemSubtitle>
                  <template #append>
                    <StatusChip
                      domain="appointment"
                      :state="appointment.status"
                    />
                    <VIcon
                      icon="ri-arrow-right-s-line"
                      class="ms-1 text-medium-emphasis"
                      aria-hidden="true"
                    />
                  </template>
                </VListItem>
              </VList>
              <div class="mt-4">
                <VBtn
                  variant="tonal"
                  @click="openAppointments"
                >
                  {{ $t('dashboard.schedule.viewAll') }}
                </VBtn>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Needs attention -->
      <VCol cols="12">
        <VCard
          flat
          border
          data-tour="dashboard-needs-attention"
        >
          <VCardItem>
            <VCardTitle>{{ $t('dashboard.needsAttention.title') }}</VCardTitle>
            <VCardSubtitle>{{ $t('dashboard.needsAttention.subtitle') }}</VCardSubtitle>
          </VCardItem>
          <VCardText>
            <div
              v-if="showInitialLoading"
              class="text-medium-emphasis"
              role="status"
            >
              {{ $t('dashboard.needsAttention.loading') }}
            </div>

            <VAlert
              v-else-if="store.needsAttentionError"
              type="error"
              variant="tonal"
              role="alert"
            >
              {{ sectionMessage(store.needsAttentionError) }}
              <template #append>
                <VBtn
                  variant="text"
                  size="small"
                  :aria-label="$t('dashboard.retry')"
                  @click="refresh"
                >
                  {{ $t('dashboard.retry') }}
                </VBtn>
              </template>
            </VAlert>

            <div
              v-else-if="store.isNeedsAttentionEmpty"
              role="status"
            >
              <VAlert
                type="info"
                variant="tonal"
              >
                {{ $t('dashboard.needsAttention.empty') }}
              </VAlert>
            </div>

            <div
              v-else
              aria-live="polite"
            >
              <VList
                class="pa-0"
                lines="two"
              >
                <VListItem
                  v-for="invoice in store.needsAttention.overdue_invoices"
                  :key="`invoice-${invoice.id}`"
                  :to="{ name: 'clients-id', params: { id: invoice.client_id }, query: { section: 'billing' } }"
                  :aria-label="$t('dashboard.needsAttention.viewInvoiceAria', {
                    client: invoice.client_name || $t('appointments.unknownClient'),
                  })"
                >
                  <template #prepend>
                    <StatusChip
                      domain="invoice"
                      state="overdue"
                      class="me-3"
                    />
                  </template>
                  <VListItemTitle>
                    {{ invoice.client_name || $t('appointments.unknownClient') }}
                  </VListItemTitle>
                  <VListItemSubtitle>
                    {{ $t('dashboard.needsAttention.overdueInvoice') }}
                    · {{ formatCurrency(invoice.amount_cents, invoice.currency) }}
                    <template v-if="invoice.due_on">
                      · {{ formatDate(invoice.due_on, { day: 'numeric', month: 'short', year: 'numeric' }, 'nl') }}
                    </template>
                  </VListItemSubtitle>
                  <template #append>
                    <span class="text-caption text-medium-emphasis me-1">
                      {{ $t('dashboard.needsAttention.viewAction') }}
                    </span>
                    <VIcon
                      icon="ri-arrow-right-s-line"
                      class="text-medium-emphasis"
                      aria-hidden="true"
                    />
                  </template>
                </VListItem>

                <VListItem
                  v-for="subscription in store.needsAttention.past_due_subscriptions"
                  :key="`subscription-${subscription.id}`"
                  :to="{ name: 'clients-id', params: { id: subscription.client_id }, query: { section: 'billing' } }"
                  :aria-label="$t('dashboard.needsAttention.viewSubscriptionAria', {
                    client: subscription.client_name || $t('appointments.unknownClient'),
                  })"
                >
                  <template #prepend>
                    <StatusChip
                      domain="subscription"
                      state="past_due"
                      class="me-3"
                    />
                  </template>
                  <VListItemTitle>
                    {{ subscription.client_name || $t('appointments.unknownClient') }}
                  </VListItemTitle>
                  <VListItemSubtitle>
                    {{ $t('dashboard.needsAttention.pastDueSubscription') }}
                  </VListItemSubtitle>
                  <template #append>
                    <span class="text-caption text-medium-emphasis me-1">
                      {{ $t('dashboard.needsAttention.viewAction') }}
                    </span>
                    <VIcon
                      icon="ri-arrow-right-s-line"
                      class="text-medium-emphasis"
                      aria-hidden="true"
                    />
                  </template>
                </VListItem>

                <VListItem
                  v-for="risk in store.needsAttention.elevated_no_show_risks"
                  :key="`risk-${risk.id}`"
                  :to="{ name: 'appointments' }"
                  :aria-label="$t('dashboard.needsAttention.viewRiskAria', {
                    client: risk.client_name || $t('appointments.unknownClient'),
                  })"
                >
                  <template #prepend>
                    <StatusChip
                      domain="no_show_risk"
                      state="elevated"
                      class="me-3"
                    />
                  </template>
                  <VListItemTitle>
                    {{ risk.client_name || $t('appointments.unknownClient') }}
                  </VListItemTitle>
                  <VListItemSubtitle>
                    {{ $t('dashboard.needsAttention.elevatedNoShowRisk') }}
                    <template v-if="risk.starts_at">
                      · {{ formatDate(risk.starts_at, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }, 'nl') }}
                    </template>
                  </VListItemSubtitle>
                  <template #append>
                    <span class="text-caption text-medium-emphasis me-1">
                      {{ $t('dashboard.needsAttention.viewRiskAction') }}
                    </span>
                    <VIcon
                      icon="ri-arrow-right-s-line"
                      class="text-medium-emphasis"
                      aria-hidden="true"
                    />
                  </template>
                </VListItem>

                <VListItem
                  v-for="insight in store.needsAttention.coaching_insights"
                  :key="`insight-${insight.id}`"
                  :to="{ name: 'insights' }"
                  :aria-label="$t('dashboard.needsAttention.viewInsightAria', {
                    client: insight.client_name || $t('appointments.unknownClient'),
                  })"
                >
                  <template #prepend>
                    <StatusChip
                      domain="coaching_insight_category"
                      :state="insight.category"
                      class="me-3"
                    />
                  </template>
                  <VListItemTitle>
                    {{ insight.client_name || $t('appointments.unknownClient') }}
                  </VListItemTitle>
                  <VListItemSubtitle>
                    {{ insight.body }}
                  </VListItemSubtitle>
                  <template #append>
                    <span class="text-caption text-medium-emphasis me-1">
                      {{ $t('dashboard.needsAttention.viewInsightAction') }}
                    </span>
                    <VIcon
                      icon="ri-arrow-right-s-line"
                      class="text-medium-emphasis"
                      aria-hidden="true"
                    />
                  </template>
                </VListItem>
              </VList>

              <p
                v-if="moreOverdueCount > 0"
                class="text-medium-emphasis mt-3 mb-0"
              >
                {{ $t('dashboard.needsAttention.moreInvoices', { count: moreOverdueCount }) }}
              </p>
              <p
                v-if="morePastDueCount > 0"
                class="text-medium-emphasis mt-1 mb-0"
              >
                {{ $t('dashboard.needsAttention.moreSubscriptions', { count: morePastDueCount }) }}
              </p>
              <p
                v-if="moreRiskCount > 0"
                class="text-medium-emphasis mt-1 mb-0"
              >
                {{ $t('dashboard.needsAttention.moreRisks', { count: moreRiskCount }) }}
              </p>
              <p
                v-if="moreInsightsCount > 0"
                class="text-medium-emphasis mt-1 mb-0"
              >
                {{ $t('dashboard.needsAttention.moreInsights', { count: moreInsightsCount }) }}
              </p>

              <div class="mt-4 d-flex flex-wrap gap-2">
                <VBtn
                  variant="tonal"
                  @click="openRoster"
                >
                  {{ $t('dashboard.needsAttention.viewAll') }}
                </VBtn>
                <VBtn
                  v-if="(store.needsAttention.elevated_no_show_risks_total || 0) > 0"
                  variant="text"
                  @click="openAppointments"
                >
                  {{ $t('dashboard.needsAttention.viewRiskAction') }}
                </VBtn>
                <VBtn
                  v-if="(store.needsAttention.coaching_insights_total || 0) > 0"
                  variant="text"
                  @click="openInsights"
                >
                  {{ $t('dashboard.needsAttention.viewInsightAction') }}
                </VBtn>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Plan follow-up (F-036) — distinct from billing/insight Needs Attention -->
      <VCol cols="12">
        <VCard
          flat
          border
        >
          <VCardItem>
            <VCardTitle>{{ $t('dashboard.coachingAttention.title') }}</VCardTitle>
            <VCardSubtitle>
              {{ $t('dashboard.coachingAttention.subtitle') }}
              · {{ $t('dashboard.coachingAttention.periodLabel', {
                days: store.coachingAttention.period?.days || 7,
              }) }}
            </VCardSubtitle>
          </VCardItem>
          <VCardText>
            <div
              v-if="showInitialLoading"
              class="text-medium-emphasis"
              role="status"
            >
              {{ $t('dashboard.coachingAttention.loading') }}
            </div>

            <VAlert
              v-else-if="store.coachingAttentionError"
              type="error"
              variant="tonal"
              role="alert"
            >
              {{ sectionMessage(store.coachingAttentionError) }}
              <template #append>
                <VBtn
                  variant="text"
                  size="small"
                  :aria-label="$t('dashboard.retry')"
                  @click="refresh"
                >
                  {{ $t('dashboard.retry') }}
                </VBtn>
              </template>
            </VAlert>

            <div
              v-else-if="store.isCoachingAttentionEmpty"
              role="status"
            >
              <VAlert
                type="success"
                variant="tonal"
              >
                {{ $t('dashboard.coachingAttention.empty') }}
              </VAlert>
            </div>

            <div
              v-else
              aria-live="polite"
            >
              <VList
                class="pa-0"
                lines="two"
              >
                <VListItem
                  v-for="item in store.coachingAttention.items"
                  :key="`coaching-attention-${item.coaching_relationship_id}`"
                  :to="{
                    name: 'clients-id',
                    params: { id: item.client_id },
                    query: { section: 'coaching' },
                  }"
                  :aria-label="$t('dashboard.coachingAttention.rowAria', {
                    client: item.client_name || $t('appointments.unknownClient'),
                    gaps: gapsLabel(item),
                  })"
                >
                  <template #prepend>
                    <VChip
                      size="small"
                      variant="tonal"
                      class="me-3"
                      :color="worstSeverity(item) === 'very_behind' ? 'error' : 'warning'"
                    >
                      {{ severityLabel(worstSeverity(item)) }}
                    </VChip>
                  </template>
                  <VListItemTitle>
                    {{ item.client_name || $t('appointments.unknownClient') }}
                  </VListItemTitle>
                  <VListItemSubtitle>
                    {{ gapsLabel(item) }}
                  </VListItemSubtitle>
                  <template #append>
                    <span class="text-caption text-medium-emphasis me-1">
                      {{ $t('dashboard.coachingAttention.viewClient') }}
                    </span>
                    <VIcon
                      icon="ri-arrow-right-s-line"
                      class="text-medium-emphasis"
                      aria-hidden="true"
                    />
                  </template>
                </VListItem>
              </VList>

              <p
                v-if="moreCoachingAttentionCount > 0"
                class="text-medium-emphasis mt-3 mb-0"
              >
                {{ $t('dashboard.coachingAttention.more', { count: moreCoachingAttentionCount }) }}
              </p>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Roster -->
      <VCol
        cols="12"
        md="6"
      >
        <VCard
          flat
          border
          class="h-100"
          data-tour="dashboard-roster"
        >
          <VCardItem>
            <VCardTitle>
              {{ $t('dashboard.roster.title') }}
            </VCardTitle>
            <VCardSubtitle>
              {{ $t('dashboard.roster.subtitle') }}
            </VCardSubtitle>
          </VCardItem>

          <VCardText>
            <div
              v-if="showInitialLoading"
              class="text-medium-emphasis"
              role="status"
            >
              {{ $t('dashboard.roster.loading') }}
            </div>

            <VAlert
              v-else-if="store.rosterError"
              type="error"
              variant="tonal"
              role="alert"
            >
              {{ sectionMessage(store.rosterError) }}
              <template #append>
                <VBtn
                  variant="text"
                  size="small"
                  :aria-label="$t('dashboard.roster.retry')"
                  @click="refresh"
                >
                  {{ $t('dashboard.roster.retry') }}
                </VBtn>
              </template>
            </VAlert>

            <div
              v-else-if="store.isRosterEmpty"
              role="status"
            >
              <VAlert
                type="info"
                variant="tonal"
                class="mb-4"
              >
                {{ $t('dashboard.roster.empty') }}
              </VAlert>
              <VBtn
                color="primary"
                @click="openRoster"
              >
                {{ $t('dashboard.roster.emptyCta') }}
              </VBtn>
            </div>

            <div
              v-else
              class="d-flex flex-column gap-4"
              aria-live="polite"
            >
              <RouterLink
                v-for="row in rosterRows"
                :key="row.state"
                :to="{ name: 'clients', query: { relationship_lifecycle_state: row.state } }"
                class="d-flex align-center justify-space-between text-decoration-none text-high-emphasis"
                :aria-label="t('dashboard.roster.countAria', {
                  state: relationshipLifecycleChipLabel(row.state),
                  count: row.count,
                })"
              >
                <StatusChip
                  domain="relationship"
                  :state="row.state"
                />
                <span class="d-flex align-center gap-1">
                  <span class="text-h5 font-weight-medium">{{ row.count }}</span>
                  <VIcon
                    icon="ri-arrow-right-s-line"
                    class="text-medium-emphasis"
                    aria-hidden="true"
                  />
                </span>
              </RouterLink>

              <div>
                <VBtn
                  variant="tonal"
                  @click="openRoster"
                >
                  {{ $t('dashboard.roster.viewAll') }}
                </VBtn>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Recent clients -->
      <VCol
        cols="12"
        md="6"
      >
        <VCard
          flat
          border
          class="h-100"
        >
          <VCardItem>
            <VCardTitle>{{ $t('dashboard.recentClients.title') }}</VCardTitle>
            <VCardSubtitle>{{ $t('dashboard.recentClients.subtitle') }}</VCardSubtitle>
          </VCardItem>
          <VCardText>
            <div
              v-if="showInitialLoading"
              class="text-medium-emphasis"
              role="status"
            >
              {{ $t('dashboard.recentClients.loading') }}
            </div>

            <VAlert
              v-else-if="store.recentClientsError"
              type="error"
              variant="tonal"
              role="alert"
            >
              {{ sectionMessage(store.recentClientsError) }}
              <template #append>
                <VBtn
                  variant="text"
                  size="small"
                  :aria-label="$t('dashboard.retry')"
                  @click="refresh"
                >
                  {{ $t('dashboard.retry') }}
                </VBtn>
              </template>
            </VAlert>

            <div
              v-else-if="store.isRecentClientsEmpty"
              role="status"
            >
              <VAlert
                type="info"
                variant="tonal"
                class="mb-4"
              >
                {{ $t('dashboard.recentClients.empty') }}
              </VAlert>
              <VBtn
                color="primary"
                @click="openRoster"
              >
                {{ $t('dashboard.recentClients.emptyCta') }}
              </VBtn>
            </div>

            <div
              v-else
              aria-live="polite"
            >
              <VList
                class="pa-0"
                lines="two"
              >
                <VListItem
                  v-for="client in store.recentClients"
                  :key="client.id"
                  :to="{ name: 'clients-id', params: { id: client.id } }"
                  :aria-label="$t('dashboard.recentClients.rowAria', {
                    client: client.name || $t('appointments.unknownClient'),
                    state: relationshipLifecycleChipLabel(client.relationship_lifecycle_state),
                  })"
                >
                  <VListItemTitle>
                    {{ client.name || $t('appointments.unknownClient') }}
                  </VListItemTitle>
                  <VListItemSubtitle>
                    {{ relationshipLifecycleChipLabel(client.relationship_lifecycle_state) }}
                  </VListItemSubtitle>
                  <template #append>
                    <StatusChip
                      domain="relationship"
                      :state="client.relationship_lifecycle_state"
                    />
                    <VIcon
                      icon="ri-arrow-right-s-line"
                      class="ms-1 text-medium-emphasis"
                      aria-hidden="true"
                    />
                  </template>
                </VListItem>
              </VList>
              <div class="mt-4">
                <VBtn
                  variant="tonal"
                  @click="openRoster"
                >
                  {{ $t('dashboard.recentClients.viewAll') }}
                </VBtn>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
